import { z } from "zod";
import { containerSchema } from "./container.js";
import { ERROR_CODES } from "./errors.js";
import { descendantsOf } from "./tree.js";
/**
 * Product cap on one bulk move (ADR-0040). Enforced by the request schemas and
 * mirrored by the UI selection; the schemas are the authority.
 */
export const BULK_MOVE_MAX_ROWS = 50;
/** One selected container plus the version the selection was built from. */
export const bulkMoveContainerRowSchema = z.object({
    id: z.string().uuid(),
    version: z.number().int().nonnegative(),
});
/**
 * `POST /api/containers/bulk-move` body (ADR-0040). Every selected container
 * lands under `parentId` of the destination `entryId`, or becomes a root when
 * `parentId` is null. A destination in another home is an ownership transfer:
 * the parent is then null and `confirmTransfer` must be true.
 */
export const bulkMoveContainersSchema = z.object({
    containers: z.array(bulkMoveContainerRowSchema).min(1).max(BULK_MOVE_MAX_ROWS),
    entryId: z.string().uuid(),
    parentId: z.string().uuid().nullable(),
    confirmTransfer: z.boolean().optional(),
});
/**
 * `POST /api/containers/:id/items/bulk-move` body (ADR-0040): placements in the
 * path container move to `targetContainerId`, carrying their full quantity.
 * Source and target must share a home; placements carry no version.
 */
export const bulkMoveItemsSchema = z.object({
    itemIds: z.array(z.string().uuid()).min(1).max(BULK_MOVE_MAX_ROWS),
    targetContainerId: z.string().uuid(),
});
/**
 * A row the batch could not move. `code` reuses the canonical API error codes
 * plus `aborted` for rows never attempted after an earlier commit failure, so
 * the client can distinguish "failed" from "not reached" without inventing a
 * second code vocabulary.
 */
export const bulkMoveFailureCodeSchema = z.union([
    z.enum(ERROR_CODES),
    z.literal("aborted"),
]);
export const bulkMoveFailureSchema = z.object({
    id: z.string(),
    code: bulkMoveFailureCodeSchema,
    message: z.string(),
});
/**
 * Container batch outcome: the rows that moved, the rows that failed, and the
 * summed item placements dropped by cross-home transfers (ADR-0040).
 */
export const bulkMoveContainerResultSchema = z.object({
    moved: z.array(containerSchema),
    failed: z.array(bulkMoveFailureSchema),
    droppedPlacements: z.number().int().nonnegative(),
});
/**
 * Item batch outcome: each moved placement's destination quantity (merged when
 * the item was already there) and the per-row failures.
 */
export const bulkMoveItemResultSchema = z.object({
    moved: z.array(z.object({ itemId: z.string().uuid(), quantity: z.number().int().positive() })),
    failed: z.array(bulkMoveFailureSchema),
});
/**
 * Structural guards for any bulk selection, shared by the client (disables the
 * action) and the server (pre-validates before a write). Checks, in order:
 * empty selection, cap exceeded, duplicate rows. Tree shape is not consulted;
 * use {@link containerBulkSelectionProblem} for container selections.
 *
 * @param ids selected row ids in selection order.
 * @returns the first problem, or null when the selection is valid.
 */
export function bulkSelectionProblem(ids) {
    if (ids.length === 0) {
        return { kind: "empty", ids: [], message: "Select at least one row" };
    }
    if (ids.length > BULK_MOVE_MAX_ROWS) {
        return {
            kind: "too_many",
            ids: ids.slice(BULK_MOVE_MAX_ROWS),
            message: `A bulk move is limited to ${BULK_MOVE_MAX_ROWS} rows (selected ${ids.length})`,
        };
    }
    const seen = new Set();
    const duplicates = new Set();
    for (const id of ids) {
        if (seen.has(id))
            duplicates.add(id);
        else
            seen.add(id);
    }
    if (duplicates.size > 0) {
        return {
            kind: "duplicate",
            ids: [...duplicates],
            message: "The selection contains duplicate rows",
        };
    }
    return null;
}
/**
 * Toggle one row in a capped bulk selection. Removing a selected row always
 * works; adding a new row is refused once the cap is reached, so the client
 * never grows a batch the schema would reject.
 *
 * @param current the current selection.
 * @param id the row to toggle.
 * @param cap the product row cap (default {@link BULK_MOVE_MAX_ROWS}).
 * @returns the next selection (the same set when the cap blocked an add).
 */
export function toggleBulkSelection(current, id, cap = BULK_MOVE_MAX_ROWS) {
    const next = new Set(current);
    if (next.has(id)) {
        next.delete(id);
        return next;
    }
    if (next.size >= cap)
        return current;
    next.add(id);
    return next;
}
/**
 * Select-all toggle over the visible rows: selects up to the cap, or clears
 * the selection when every visible row is already selected.
 *
 * @param visibleIds the rows currently offered by the list, in display order.
 * @param current the current selection.
 * @param cap the product row cap (default {@link BULK_MOVE_MAX_ROWS}).
 * @returns the next selection.
 */
export function selectAllBulkSelection(visibleIds, current, cap = BULK_MOVE_MAX_ROWS) {
    if (visibleIds.length > 0 && visibleIds.every((id) => current.has(id))) {
        return new Set();
    }
    return new Set(visibleIds.slice(0, cap));
}
/**
 * Selection guards for a container batch: {@link bulkSelectionProblem} plus the
 * tree rule that one selected container may not contain another (moving both
 * would move the descendant twice). The reported `ids` are the offending
 * ancestor/descendant pair, ancestor first.
 *
 * @param ids selected container ids in selection order.
 * @param containers the flat catalog the tree is derived from.
 * @returns the first problem, or null when the selection is valid.
 */
export function containerBulkSelectionProblem(ids, containers) {
    const structural = bulkSelectionProblem(ids);
    if (structural)
        return structural;
    for (const candidate of ids) {
        const descendantIds = new Set(descendantsOf(containers, candidate).map((c) => c.id));
        for (const other of ids) {
            if (other !== candidate && descendantIds.has(other)) {
                return {
                    kind: "ancestor_descendant",
                    ids: [candidate, other],
                    message: "A selected container cannot contain another selected container",
                };
            }
        }
    }
    return null;
}
//# sourceMappingURL=bulk-move.js.map